import { Link } from "react-router-dom";

export default function Privacy() {
  const updated = "14 June 2026";
  return (
    <div className="min-h-screen bg-[#F3F0EA] text-[#2A332C]">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <Link to="/" className="text-sm text-[#5C6B61] hover:text-[#3B5A3E]" data-testid="privacy-back-link">
          ← Back
        </Link>
        <header className="mt-6 mb-10">
          <div className="ff-label">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-[#5C6B61] mt-3">Last updated: {updated}</p>
        </header>

        <section className="prose prose-sm max-w-none space-y-6 text-[15px] leading-relaxed" data-testid="privacy-content">
          <p>
            Shop, Cal & Meal (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) operates the
            Shop, Cal & Meal web app at <strong>smartershop.xyz</strong> and the Shop, Cal & Meal mobile app for Android and iOS. This policy explains what data we collect, why we collect it, and the rights you have over it.
          </p>

          <Section id="contact" title="1. Who to contact">
            <p>
              Data controller: Shop, Cal & Meal. Email <a href="mailto:hello@smartershop.xyz" className="text-[#3B5A3E] underline">hello@smartershop.xyz</a> for any privacy questions or to exercise your rights.
            </p>
          </Section>

          <Section id="data" title="2. What we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Account data:</strong> name, email, password (hashed), sign-in provider.</li>
              <li><strong>Profile &amp; health data:</strong> age, sex, height, weight, activity level, calorie goal &mdash; only what you enter on the Calorie Target page.</li>
              <li><strong>Food &amp; shopping data:</strong> saved recipes, shopping lists, planner entries, saved foods, calorie log entries, scan results.</li>
              <li><strong>Photos you scan:</strong> images you submit to the food scanner are sent to Anthropic Claude for analysis and are <em>not</em> stored on our servers.</li>
              <li><strong>Payment data:</strong> handled entirely by Stripe. We receive only the subscription status and a reference id &mdash; we never see your full card number.</li>
              <li><strong>Technical data:</strong> source IP (for abuse prevention on the contact form), browser / device user-agent, app version.</li>
            </ul>
          </Section>

          <Section id="why" title="3. Why we use it (lawful bases)">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Contract:</strong> to deliver the features you sign up for &mdash; recipes, shopping lists, calorie tracking, Pro subscription.</li>
              <li><strong>Legitimate interest:</strong> abuse prevention, debugging, product improvement.</li>
              <li><strong>Consent:</strong> non-essential emails (weekly recap), if you opt in.</li>
            </ul>
          </Section>

          <Section id="sharing" title="4. Who we share data with">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Anthropic (Claude):</strong> for AI recipe parsing, food scan analysis and basket-health scoring. No personally-identifying info is sent &mdash; only the recipe text / photo / item list.</li>
              <li><strong>Stripe:</strong> for subscription payments.</li>
              <li><strong>Resend:</strong> for transactional email (welcome, contact replies, subscription confirmations, weekly recaps).</li>
              <li><strong>Google / Apple:</strong> if you choose Sign in with Google or use the mobile app.</li>
              <li><strong>MongoDB Atlas:</strong> our database provider (EU region).</li>
            </ul>
            <p>We do <strong>not</strong> sell your data, and we do <strong>not</strong> use it for advertising.</p>
          </Section>

          <Section id="retention" title="5. How long we keep it">
            <p>
              Account data is kept while your account is active. When you delete your account, all of your data is removed within 24 hours (a 24-hour restore window covers accidental deletes). Anonymous aggregate metrics (e.g. &ldquo;3 scans were performed today&rdquo;) may be retained.
            </p>
          </Section>

          <Section id="rights" title="6. Your rights (UK GDPR / EU GDPR)">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Access:</strong> ask us for a copy of the data we hold on you.</li>
              <li><strong>Rectification:</strong> correct anything that&apos;s wrong from your Profile page.</li>
              <li><strong>Erasure (&ldquo;right to be forgotten&rdquo;):</strong> use <em>Profile → Delete my account</em> in the app, or email us.</li>
              <li><strong>Portability:</strong> ask for an export of your recipes / planners / lists.</li>
              <li><strong>Objection / withdraw consent:</strong> turn off the weekly recap email any time in Profile.</li>
              <li><strong>Complaint:</strong> to the UK ICO at <a href="https://ico.org.uk/concerns" className="text-[#3B5A3E] underline" target="_blank" rel="noopener noreferrer">ico.org.uk/concerns</a>.</li>
            </ul>
          </Section>

          <Section id="delete" title="7. Account deletion">
            <p>
              You can delete your Shop, Cal & Meal account at any time from <strong>Profile → Delete my account</strong> inside the app. This removes your account data and every associated record (recipes, lists, planner, calorie log, scan history, payment references) within 24 hours.
            </p>
            <p>
              You can also request deletion by emailing <a href="mailto:hello@smartershop.xyz" className="text-[#3B5A3E] underline">hello@smartershop.xyz</a> from your registered address.
            </p>
          </Section>

          <Section id="security" title="8. Security">
            <p>
              Passwords are stored as bcrypt hashes. All traffic is TLS-encrypted. Access to production databases is restricted to maintainers.
            </p>
          </Section>

          <Section id="children" title="9. Children">
            <p>
              Shop, Cal & Meal is not intended for children under 13. If you believe a child has signed up, please contact us and we&apos;ll remove the account.
            </p>
          </Section>

          <Section id="changes" title="10. Changes to this policy">
            <p>
              We&apos;ll post material changes on this page and update the &ldquo;Last updated&rdquo; date. Continued use of Shop, Cal & Meal after a change constitutes acceptance.
            </p>
          </Section>
        </section>

        <footer className="mt-12 pt-6 border-t border-[#E5E2DA] text-xs text-[#8B9690] flex items-center gap-3 flex-wrap">
          <span>© Shop, Cal & Meal</span>
          <span>·</span>
          <Link to="/terms" className="hover:text-[#3B5A3E]" data-testid="privacy-link-terms">Terms of Service</Link>
          <span>·</span>
          <a href="mailto:hello@smartershop.xyz" className="hover:text-[#3B5A3E]">hello@smartershop.xyz</a>
        </footer>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-xl font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
      <div className="text-[#3C4842]">{children}</div>
    </section>
  );
}
